// session-preflight-guard-active-checks.ts
//
// F4 checks — "is a guard actually watching?", as opposed to "do the declared
// hook files exist?" — split out of session-preflight-checks.ts (plan
// 20260924-0205, 200-line budget). Behaviour unchanged.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import { pathExists } from "../filesystem-helpers.js";
import type { PreflightResult, SettingsHooksLike } from "./session-preflight-types.js";

/**
 * Audit 2026-09-14 (F4): mission mới đăng ký 0 hook mà preflight vẫn `{ok:true}`.
 *
 * `checkHookCommandsExist` (session-preflight-hook-file-checks.ts) duyệt `settings.hooks ?? {}` — rỗng nghĩa là
 * vòng lặp rỗng, nghĩa là "không thiếu file hook nào", đọc ra thành ĐẠT. Nó
 * không có khả năng fail về mặt cấu trúc. Check này hỏi câu khác hẳn: những
 * hook autopilot BẮT BUỘC có thật sự được đăng ký chưa.
 *
 * So theo BASENAME: command thật mang `${CLAUDE_PROJECT_DIR}` chưa resolve và
 * tiền tố `python3`, nên so nguyên chuỗi sẽ giòn vô ích.
 */
export async function checkRequiredAutopilotHooksRegistered(
  missionWorkspace: string,
  requiredHookFiles: readonly string[],
  packRoot?: string,
): Promise<PreflightResult> {
  if (!missionWorkspace) {
    throw new Error("checkRequiredAutopilotHooksRegistered: missionWorkspace là bắt buộc.");
  }

  const settingsPath = join(missionWorkspace, ".claude", "settings.json");
  if (!(await pathExists(settingsPath))) {
    return { ok: false, reason: "settings_missing", details: settingsPath };
  }

  let settings: SettingsHooksLike;
  try {
    settings = JSON.parse(await fs.readFile(settingsPath, "utf8")) as SettingsHooksLike;
  } catch (err) {
    return {
      ok: false,
      reason: "settings_malformed_json",
      details: err instanceof Error ? err.message : String(err),
    };
  }

  // Chỉ đếm hook đăng ký dưới `PreToolUse`. Cùng basename nằm ở `PostToolUse`
  // (hoặc event khác) chạy SAU tool call ⇒ không chặn được gì, nhưng vẫn làm
  // settings.json trông "đã cấu hình" — đúng hình dạng F4 ở một lớp khác
  // (code review 2026-09-14, MEDIUM #1).
  const registered: string[] = [];
  for (const [event, matchers] of Object.entries(settings.hooks ?? {})) {
    if (event !== "PreToolUse") continue;
    for (const matcher of matchers) {
      for (const hookEntry of matcher.hooks ?? []) {
        if (hookEntry.command) registered.push(hookEntry.command);
      }
    }
  }

  // Chỉ đòi hook mà pack THẬT SỰ có file. Pack hợp lệ được phép không mang WHW
  // (tool tắt được), và bản đầu của check này đòi đủ cả hai vô điều kiện ⇒ chặn
  // sạch mọi mission có pack như vậy (đo được: 10 e2e file + probe đối chứng
  // `honest` đều blocked). Thiếu `packRoot` ⇒ giữ nguyên hành vi cũ cho caller
  // chưa cập nhật.
  const shipped: string[] = [];
  for (const relPath of requiredHookFiles) {
    if (packRoot === undefined || (await pathExists(join(packRoot, relPath)))) {
      shipped.push(relPath);
    }
  }

  const missing = shipped.filter((relPath) => {
    const basename = relPath.split("/").pop() as string;
    return !registered.some((command) => command.includes(basename));
  });

  if (missing.length > 0) {
    return {
      ok: false,
      reason: "autopilot_hooks_not_registered",
      details:
        `hook autopilot chưa đăng ký trong settings.json: ${missing.join(", ")} — ` +
        "mission sẽ chạy KHÔNG có hook giới hạn path/dangerous-command.",
    };
  }
  return { ok: true };
}

/**
 * `guard_enabled()` của pack (hooks/pretooluse-dangerous-command-guard-hook.py)
 * đọc ĐÚNG file cờ này và `exit 0` TRƯỚC KHI đọc stdin nếu nó vắng — guard
 * chuyển OPT-IN từ v1.2.6. Nghĩa là settings.json khai đủ hook mà thiếu cờ thì
 * guard im lặng tuyệt đối: đúng hình dạng F4 (báo "đã cấu hình" trong khi
 * không có gì đang canh), chỉ khác chỗ biểu hiện.
 *
 * `mission-workspace.ts` ghi cờ này lúc dựng mission; check ở đây bắt ca nó bị
 * xoá/không ghi được sau đó.
 */
export async function checkDangerousGuardOptInFlag(
  missionWorkspace: string,
): Promise<PreflightResult> {
  if (!missionWorkspace) {
    throw new Error("checkDangerousGuardOptInFlag: missionWorkspace là bắt buộc.");
  }

  const flagPath = join(missionWorkspace, ".claude", "state", "dangerous-command-guard.on");
  if (!(await pathExists(flagPath))) {
    return {
      ok: false,
      reason: "dangerous_command_guard_not_enabled",
      details:
        `thiếu cờ opt-in ${flagPath} — hook dangerous-command-guard sẽ exit 0 im lặng ` +
        "(guard_enabled() đọc đúng file này), mission chạy không có guard.",
    };
  }
  return { ok: true };
}
